import { theme } from '@prose-ui';
import { keyframes, legacyTheme, styled } from '@prose-ui/legacy';
import { withStyles } from 'legacyStyles';

import { Typography } from 'Components/Typography';

import HeroVideo from 'Blocks/HeroVideo';

import { HeroSectionContent as content } from 'assets/content/landingPages/stylingGel';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxHeight: `calc(100vh - ${legacyTheme.props.headerHeight})`,
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    [legacyTheme.breakpoints.match('sm')]: {
      minHeight: legacyTheme.props.fullHeight,
    },
  },
  heroBlock: {
    width: '100%',
    height: '100%',
    justifyContent: 'space-between',
  },
  heroVideo: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100%',
    height: 'auto',
    transform: 'translateX(-50%) translateY(-50%)',
    [legacyTheme.breakpoints.match('sm')]: {
      width: 'auto',
      height: '100vh',
    },
  },
} as const;

const TextContainer = styled.div`
  height: calc(100vh - 95px);
  padding: ${legacyTheme.spacing.s32};
  padding-bottom: ${legacyTheme.spacing.s96};
  display: flex;
  align-items: flex-end;
  width: 100%;
  font-size: 16px;
  ${legacyTheme.breakpoints.up('lg')} {
    align-items: center;
    padding: ${legacyTheme.spacing.s64};
  }
`;

const StyledTextHeroCaption = styled(Typography)`
  font-family: ${legacyTheme.typography.simplonMono};
  line-height: 1.5em;
  letter-spacing: 1;
  text-transform: uppercase;
  font-weight: 400;
  color: ${theme.colors.neutral[200]};
`;

const BouncingArrowContainer = styled('div')`
  width: 32px;
  align-self: flex-end;
  ${legacyTheme.breakpoints.up('lg')} {
    width: 38px;
  }
`;

const bounce = keyframes`
  from, to {
    transform: translateY(0%);
  }
  50% {
    transform: translateY(25%);
  }
`;

const BouncingArrow = styled('img')`
  animation: ${bounce} 1s ease-in-out infinite;
`;

const StyledTextHeroTitle = styled(Typography)`
  font-family: ${legacyTheme.typography.simplonMono};
  line-height: 1.5em;
  letter-spacing: 1;
  text-transform: uppercase;
  font-weight: 400;
  font-size: 24px;
  max-width: 70%;
  margin-bottom: ${legacyTheme.spacing.s32};

  color: ${theme.colors.neutral[200]};
  ${legacyTheme.breakpoints.up('sm')} {
    font-size: 36px;
  }
`;

type HeroSectionProps = {
  classes?: Record<string, string>;
};

const HeroSection = ({ classes }: HeroSectionProps) => {
  const { headline, subHead, arrowDown, videoMobile, videoDesktop, fallbackImage } = content;
  return (
    // @ts-expect-error HeroVideo is not typed yet
    <HeroVideo
      classes={{
        root: classes?.root,
        heroVideo: classes?.heroVideo,
        heroBlock: classes?.heroBlock,
      }}
      imageDesktop={fallbackImage.src}
      imageMobile={fallbackImage.src}
      videoDesktop={videoDesktop}
      videoMobile={videoMobile}
    >
      <TextContainer>
        <div>
          <StyledTextHeroCaption paragraph variant="mono1">
            {headline}
          </StyledTextHeroCaption>

          <StyledTextHeroTitle>{subHead}</StyledTextHeroTitle>
          <BouncingArrowContainer>
            <BouncingArrow alt="arrow" src={arrowDown} />
          </BouncingArrowContainer>
        </div>
      </TextContainer>
    </HeroVideo>
  );
};

export default withStyles(HeroSection, styles);
